import React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import theme from '../theme';
import V2ContentWrapper from '../components/V2ContentWrapper';

const SECTION_TABS = {
  'building-message': [
    { label: 'Overview', path: '/playbook/building-message' },
    { label: 'Sales Deck', path: '/playbook/building-message/sales-deck-explained' },
    { label: 'Example Deck', path: '/playbook/building-message/example-sales-deck' },
    { label: 'Messaging', path: '/playbook/building-message/messaging-explained' },
    { label: 'Sales Cycle', path: '/playbook/building-message/anatomy-sales-cycle' },
  ],
  'onboarding-training': [
    { label: 'Overview', path: '/playbook/onboarding-training' },
    { label: 'Training Process', path: '/playbook/onboarding-training/training-process-explained' },
    { label: 'Example Feedback', path: '/playbook/onboarding-training/example-feedback' },
    { label: 'Training Summary', path: '/playbook/onboarding-training/training-summary' },
    { label: 'Sales Meetings', path: '/playbook/onboarding-training/example-sales-meetings' },
  ],
  'sales-activity': [
    { label: 'Overview', path: '/playbook/sales-activity' },
    { label: 'Plan Interactions', path: '/playbook/sales-activity/plan-your-interactions' },
    { label: 'Sales Activity', path: '/playbook/sales-activity/sales-activity-explained' },
    { label: 'Prospecting', path: '/playbook/sales-activity/prospecting' },
    { label: 'Communication', path: '/playbook/sales-activity/intentionality-communication' },
  ],
  'closing': [
    { label: 'Overview', path: '/playbook/closing' },
    { label: 'Proposal Process', path: '/playbook/closing/proposal-process-explained' },
    { label: 'Examples & Templates', path: '/playbook/closing/examples-templates' },
    { label: 'ABC Corp Sample', path: '/playbook/closing/abc-corp-sample-proposal' },
    { label: 'ABC Services Sample', path: '/playbook/closing/abc-services-sample-proposal' },
  ],
  'skills-leadership': [
    { label: 'Overview', path: '/playbook/skills-leadership' },
    { label: 'Comp Plans', path: '/playbook/skills-leadership/comp-plans' },
    { label: 'Org Structures', path: '/playbook/skills-leadership/organizational-structures' },
  ],
};

const PlaybookWrapper = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  // Detect which section from the URL: /playbook/{section-key}/...
  const pathParts = location.pathname.replace('/playbook/', '').split('/');
  const sectionKey = pathParts[0];
  const tabs = SECTION_TABS[sectionKey];

  const currentIndex = tabs ? tabs.findIndex(t => t.path === location.pathname) : -1;
  const nextTab = tabs && currentIndex >= 0 && currentIndex < tabs.length - 1
    ? tabs[currentIndex + 1]
    : null;
  const prevTab = tabs && currentIndex > 0
    ? tabs[currentIndex - 1]
    : null;

  const handleNav = (path) => {
    history.push(path);
    // ContentArea has overflow-y: auto, so scroll that container instead of window
    const scrollContainer = document.querySelector('[data-tour-scroll="content"]');
    if (scrollContainer) scrollContainer.scrollTop = 0;
    else window.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      <BackRow>
        <BackBtn onClick={() => history.push('/playbook')}>
          {'\u2190'} Back to Playbook
        </BackBtn>
      </BackRow>
      {tabs && (
        <TabBar>
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              $active={location.pathname === tab.path}
              onClick={() => handleNav(tab.path)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabBar>
      )}
      <V2ContentWrapper>
        {children}
      </V2ContentWrapper>
      {tabs && (
        <BottomNav>
          <div>
            {prevTab && (
              <NavBtn onClick={() => handleNav(prevTab.path)}>
                {'\u2190'} {prevTab.label}
              </NavBtn>
            )}
          </div>
          <div>
            {nextTab ? (
              <NavBtn $primary onClick={() => handleNav(nextTab.path)}>
                {nextTab.label} {'\u2192'}
              </NavBtn>
            ) : (
              <NavBtn onClick={() => history.push('/playbook')}>
                Back to Playbook {'\u2192'}
              </NavBtn>
            )}
          </div>
        </BottomNav>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
`;

const BackRow = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primaryDark};
  }
`;

const TabBar = styled.div`
  display: flex;
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: 6px;
  margin-bottom: ${theme.spacing.lg};
  gap: 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  background: ${({ $active }) => $active ? theme.colors.primary : 'transparent'};
  border: none;
  border-radius: ${theme.radii.sm};
  padding: 10px ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${({ $active }) => $active ? '#ffffff' : theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) => $active ? theme.colors.primary : theme.colors.background};
    color: ${({ $active }) => $active ? '#ffffff' : theme.colors.text};
  }

  ${theme.media.mobile} {
    padding: 8px ${theme.spacing.md};
    font-size: ${theme.typography.small.size};
  }
`;

const BottomNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.borderLight};
`;

const NavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: ${({ $primary }) => $primary ? theme.colors.primary : theme.colors.surface};
  color: ${({ $primary }) => $primary ? '#ffffff' : theme.colors.primary};
  border: ${({ $primary }) => $primary ? 'none' : `1.5px solid ${theme.colors.primary}`};
  border-radius: ${theme.radii.sm};
  padding: 10px 20px;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${({ $primary }) => $primary ? theme.colors.primaryDark : theme.colors.primaryGhost};
  }

  ${theme.media.mobile} {
    padding: 8px 14px;
    font-size: ${theme.typography.small.size};
  }
`;

export default PlaybookWrapper;
